<template>
  <v-card outlined class="rate-card">
    <div class="table-scroll">
      <table class="rate-table">
        <thead>
          <tr>
            <th rowspan="2" scope="col" class="tw-text-left currency-th">สกุลเงิน</th>
            <th v-if="buyingColumns.length" :colspan="buyingColumns.length" scope="colgroup" class="group-th">
              ซื้อ
            </th>
            <th v-if="sellingColumns.length" :colspan="sellingColumns.length" scope="colgroup" class="group-th">
              ขาย
            </th>
          </tr>
          <tr>
            <th
              v-for="(col, i) in columns"
              :key="col.fieldName"
              scope="col"
              :class="['col-th', { 'col-th--primary': col.active, 'side-start': isSideStart(i) }]"
            >
              {{ col.colName }}<span v-if="col.active" aria-hidden="true"> ★</span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="[code, rate] in rows" :key="code">
            <td>
              <div class="tw-flex tw-items-center tw-gap-3">
                <img :src="flagUrl(code)" alt="" class="tw-w-8 tw-h-6 tw-object-contain" />
                <div class="tw-flex tw-flex-col tw-leading-tight tw-min-w-0">
                  <span class="tw-font-bold">{{ code }}</span>
                  <span class="tw-text-xs tw-text-gray-600 tw-truncate">{{ countryName(code) }}</span>
                </div>
              </div>
            </td>
            <td
              v-for="(col, i) in columns"
              :key="col.fieldName"
              :class="['rate-cell', { 'rate-cell--primary': col.active, 'side-start': isSideStart(i) }]"
            >
              {{ displayRate(rate[col.fieldName]) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div class="table-footer">
      <span>
        หน่วย: <b>{{ unitName }}</b> ต่อ 1 หน่วยเงินตราต่างประเทศ · ★ คืออัตราที่ใช้บ่อย · – คือธนาคารไม่ได้ประกาศ
      </span>
      <span>อัตราอาจเปลี่ยนระหว่างวันโดยไม่แจ้งล่วงหน้า</span>
    </div>
  </v-card>
</template>

<script>
import getCountryFlagBaseUrl from "@/libs/getCountryFlagBaseUrl";
import getCountryName from "@/libs/getCountryName";

export default {
  name: "RateTable",
  props: {
    columns: { type: Array, required: true },
    // [[code, rate]], already sorted and filtered.
    rows: { type: Array, required: true },
    unitName: { type: String, default: "" },
  },
  computed: {
    buyingColumns() {
      return this.columns.filter((c) => c.type === "BUYING");
    },
    sellingColumns() {
      return this.columns.filter((c) => c.type === "SELLING");
    },
  },
  methods: {
    // First column of each side gets a divider on its left.
    isSideStart(index) {
      return index === 0 || this.columns[index].type !== this.columns[index - 1].type;
    },
    displayRate(value) {
      const text = value === null || value === undefined ? "" : String(value).trim();
      return text === "" || text === "-" ? "–" : text;
    },
    flagUrl(code) {
      return getCountryFlagBaseUrl(code);
    },
    countryName(code) {
      return getCountryName(code);
    },
  },
};
</script>

<style lang="css" scoped>
.rate-card {
  border-radius: 12px !important;
  overflow: hidden;
}

.table-scroll {
  max-height: 60vh;
  overflow: auto;
}

.rate-table {
  width: 100%;
  min-width: 720px;
  table-layout: fixed;
  /* separate, not collapse: collapsed borders belong to the table, not the
     cells, so they don't travel with the sticky header and rows scroll into
     view through them. */
  border-collapse: separate;
  border-spacing: 0;
  font-size: 14px;
}

/* Sticky header on an opaque neutral ground; the bank's colour stays in the
   bank picker, where white text on it isn't needed. */
.rate-table thead {
  position: sticky;
  top: 0;
  z-index: 1;
}

.rate-table th {
  background: #f7f8fa;
  color: #3d4450;
  font-weight: 600;
  padding: 10px 16px;
}

.currency-th {
  width: 30%;
  border-bottom: 1px solid #e3e6eb;
}

.group-th {
  text-align: center;
  padding-bottom: 4px !important;
  border-left: 1px solid #e3e6eb;
}

.col-th {
  text-align: right;
  font-size: 12px;
  font-weight: 500 !important;
  color: #5b6270 !important;
  padding-top: 4px !important;
  border-bottom: 1px solid #e3e6eb;
}

.col-th--primary {
  font-weight: 700 !important;
  color: #1a1d23 !important;
}

.rate-table td {
  padding: 10px 16px;
  border-bottom: 1px solid #eef0f3;
}

.rate-table tbody tr:hover td {
  background: #f7f8fa;
}

.rate-cell {
  text-align: right;
  font-variant-numeric: tabular-nums;
  color: #3d4450;
}

.rate-cell--primary {
  font-weight: 700;
  color: #1a1d23;
  background: #fafbfc;
}

.side-start {
  border-left: 1px solid #e3e6eb;
}

.table-footer {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 8px;
  padding: 12px 16px;
  border-top: 1px solid #e3e6eb;
  font-size: 13px;
  color: #5b6270;
}
</style>
